import { cruiseOfferTileClick, offerClicked, tourv2OfferTileClick } from 'analytics/eventDefinitions'
import { addGTMEvent, fireInteractionEvent } from 'api/googleTagManager'
import CurrencyContext from 'contexts/currencyContext'
import useOffer from 'hooks/Offers/useOffer'
import { isCruiseOffer, isLEOffer, isTourV1Offer, isTourV2Offer } from 'lib/offer/offerTypes'
import React, { useCallback, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import FrontPageTile from './FrontPageOfferTiles/FrontPageTile'
import SearchTile from './SearchOfferTiles/SearchTile'
import OfferListEventsContext, { OfferListEvents } from '../OfferListEventsContext'
import OfferListLoadingOfferTile from './OfferListLoadingOfferTile'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import HeroOfferTile from './HeroOfferTiles/HeroOfferTile'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'

interface Props {
  /**
   * The visual style of tile that should be rendered
   */
  tileStyle: 'search' | 'frontpage' | 'hero';
  /**
   * The offer ID this tile is for
   */
  offerId: string;
  /**
   * The filters associated with the list that's rendering this tile
   */
  filters?: App.OfferListFilters;
  /**
   * The position of this tile in the offer list.
   * Used for analytics
   */
  position: number;
  /**
   * Will eager load (not wait till in view) the first image of whatever image carousel is shown for this offer
   * Use this mostly for SSR
   */
  eagerLoadImage?: boolean;
  className?: string;
  /**
   * Dictate the behaviour of when the tile will mount/load
   *
   * offer-and-inview:
   * Will fetch the offer once in view
   * Will show the tile once both the offer AND the tile is in view
   * This is used to ensure the tile isn't mounted when out of view
   * Note: This will prevent the tile from being SSR'ed
   *
   * offer-or-inview:
   * Will fetch the offer once in view
   * Will show the tile as soon as the offer is available (from any source)
   * Used when you wish the tile to be able to SSR'ed
   *
   * none:
   * Will fetch the offer straight
   * Will show the tile as soon as the offer is ready
   *
   * @default none
   */
  lazyLoad?: 'offer-and-inview' | 'offer-or-inview' | 'none' ;
}

const tileStyleMap = {
  search: SearchTile,
  frontpage: FrontPageTile,
  hero: HeroOfferTile,
}

/**
 * Core offer list tile that will render the appropriate tile based on the type of item given
 */
const OfferListOfferTile = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    tileStyle,
    offerId,
    position,
    filters,
    eagerLoadImage,
    className,
    lazyLoad = 'none',
  } = props

  const [id, setOfferId] = useState<string>(offerId)
  const currency = useContext(CurrencyContext)
  const tileRef = useRef<HTMLDivElement>(null)

  // Used to lazy load the tile if turned on
  const [tileInViewRef, tileInView] = useInView({
    triggerOnce: true,
    rootMargin: '100px 100px 100px 100px',
  })

  useImperativeHandle(ref, () => tileRef.current!)

  useEffect(() => {
    tileInViewRef(tileRef.current)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileRef.current])

  const Tile = tileStyleMap[tileStyle]
  const fireOfferListEvent = useContext(OfferListEventsContext)
  const [offer, , error] = useOffer(id, {
    requireSummaryOnly: true,
    disabled: lazyLoad !== 'none' && !tileInView,
  })

  const onProductClick = useCallback(() => {
    if (isLEOffer(offer)) {
      // TODO: remove this once G4A is up?
      // we only know how to register le offer clicks at the moment
      addGTMEvent(offerClicked(offer, currency, position))
    } else if (isCruiseOffer(offer)) {
      fireInteractionEvent(cruiseOfferTileClick(offer.id))
    } else if (isTourV2Offer(offer)) {
      fireInteractionEvent(tourv2OfferTileClick(offer.id))
    }

    fireOfferListEvent(OfferListEvents.productClick, {
      offer,
      index: position,
      position,
      key: EventDataKey.ProductClick,
    })
  }, [offer, currency, position, fireOfferListEvent])

  const handleMouseEnter = useCallback(() => {
    fireOfferListEvent(OfferListEvents.hover, { offer, index: position, position, key: EventDataKey.OfferHover })
  }, [fireOfferListEvent, offer, position])

  const handleMouseLeave = useCallback(() => {
    fireOfferListEvent(OfferListEvents.hover, { offer: null, index: position, position, key: EventDataKey.OfferHover })
  }, [fireOfferListEvent, position])

  useEffect(() => {
    if (isTourV1Offer(offer) && offer.altLink?.startsWith('/tour')) {
      // if we have an tourv2, we always want to show it instead of the v1 version
      // so if our altlink is for a tourv2, then lets swap to using that offer
      const tourv2Id = offer.altLink.replace('/tour/', '')
      if (tourv2Id) {
        setOfferId(tourv2Id)
      }
    }
  }, [offer])

  useEffect(() => {
    if (tileInView && offer) {
      fireOfferListEvent(OfferListEvents.impression, {
        offer,
        index: position,
        position,
        key: EventDataKey.ProductImpression,
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileInView, offer])

  if (error) {
    return null
  }

  const showTile = lazyLoad === 'offer-and-inview' ? offer && tileInView : offer

  return (
    <ErrorBoundary fallback={null} reportErrors={false}>
      <LoaderPlayStateContext.Provider value={tileInView ? 'running' : 'paused'} >
        <div ref={tileRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {!showTile && (
            <OfferListLoadingOfferTile
              className={className}
              offerId={id}
              tileStyle={tileStyle}
            />
          )}
          {showTile && (
            <Tile
              className={className}
              offer={offer!}
              eagerLoadFirstImage={eagerLoadImage}
              filters={filters}
              productClick={onProductClick}
            />
          )}
        </div>
      </LoaderPlayStateContext.Provider>
    </ErrorBoundary>
  )
})

OfferListOfferTile.displayName = 'OfferListOfferTile'

export default React.memo(OfferListOfferTile)
